import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { IconSizesEnum, IIcon } from '../icon/icon.component';
import { I2gTooltipSequenceService } from '../../services/tooltip-sequence/i2g-tooltip-sequence.service';
import { WindowRefService } from 'i2g-ng-utils';
import {
  ITooltip,
  TooltipPointerDirectionEnum,
  TooltipType,
} from '../../services/tooltip-sequence/i2g-tooltip-sequence.data';
import {
  BASE_POINTER_DISTANCE,
  CORNER_OFFSET,
  DEFAULT_POINTER_DIRECTION,
  POINTER_SIZE,
} from './tooltip-manager.data';
import { Subscription } from 'rxjs';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';

@Component({
  selector: 'i2g-tooltip-manager',
  templateUrl: './tooltip-manager.component.html',
  styleUrls: ['./tooltip-manager.component.scss'],
})
export class TooltipManagerComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.active')
  get tooltipIsActive(): boolean {
    return this.isActive;
  }

  @Input() message: string;
  @Input() tipPosition: number;
  @Input() selector: number;

  DEFAULT_POINTER_DIRECTION = DEFAULT_POINTER_DIRECTION;

  isActive = false;
  tooltipType: TooltipType = TooltipType.onboarding;
  isTabPressed: boolean;
  isDismissible: boolean;
  showProgress: boolean;
  showNext: boolean;
  tooltips: ITooltip[];
  activeTooltipIndex: number;
  trackingObject: ITrackingObject;
  trackingScreenName: string;

  startedAction: ITrackingAction;
  completedAction: ITrackingAction;
  nextAction: ITrackingAction;
  doneAction: ITrackingAction;
  dismissAction: ITrackingAction;

  closeIcon: IIcon;

  tooltipServiceSubscription: Subscription;

  @ViewChild('tooltip', { static: false }) tooltipRef;
  @ViewChild('overlay', { static: false }) overlayRef;

  constructor(
    private _windowRefService: WindowRefService,
    private _changeDetector: ChangeDetectorRef,
    private _tooltipSequenceService: I2gTooltipSequenceService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingService: I2gNgTrackingService
  ) {}

  ngOnInit() {
    this.startedAction = this._trackingActionBuilder.buildStarted();
    this.completedAction = this._trackingActionBuilder.buildCompleted();
    this.nextAction = this._trackingActionBuilder.buildButtonTapped(
      'next',
      this.trackingScreenName
    );
    this.doneAction = this._trackingActionBuilder.buildButtonTapped(
      'done',
      this.trackingScreenName
    );
    this.dismissAction = this._trackingActionBuilder.buildButtonTapped(
      'dismiss',
      this.trackingScreenName
    );
  }

  ngAfterViewInit() {
    this._changeDetector.detach();

    this.closeIcon = {
      name: 'close-navigation',
      size: IconSizesEnum.small,
    };

    this.tooltipServiceSubscription = this._tooltipSequenceService.tooltip$.subscribe(
      (tooltipSequence) => {
        if (!tooltipSequence || tooltipSequence.tooltips.length === 0) {
          this.tooltips = null;
          this._changeDetector.detectChanges();
          return;
        }

        // if tooltips are currently null, we are initializing instead of updating the tooltips.
        // this is useful for realtime updates on storybook, not so much for actual production code
        if (!this.tooltips) {
          this.activeTooltipIndex = 0;
          this._trackingService.trackEvent(
            this.trackingObject,
            this.startedAction
          );
        }

        this.tooltipType =
          tooltipSequence.tooltipType || TooltipType.onboarding;
        this.tooltips = tooltipSequence.tooltips;
        this.isDismissible = tooltipSequence.isDismissible;
        this.showNext = tooltipSequence.tooltips.length > 1;
        this.showProgress = this.showNext && tooltipSequence.showProgress;

        this.trackingObject = tooltipSequence.trackingObject;
        this.trackingScreenName = tooltipSequence.trackingScreenName;

        this.loadActiveTooltip();
      }
    );
  }

  isLastTooltip(): boolean {
    return this.tooltips.length === this.activeTooltipIndex + 1;
  }

  isOver(): boolean {
    return !this.tooltips || this.activeTooltipIndex >= this.tooltips.length;
  }

  loadActiveTooltip() {
    this.isActive = true;
    this._changeDetector.detectChanges();

    const document = this._windowRefService.nativeWindow.document;
    const tooltipEl = this.tooltipRef.nativeElement;
    const pointerEl = tooltipEl.querySelector('.pointer');
    const tooltipStyle = tooltipEl.style;
    const pointerStyle = pointerEl?.style || {}; // required since pointer is optional for tooltips
    const activeTooltip = this.tooltips[this.activeTooltipIndex];

    const pointerPosition = activeTooltip.pointerPosition || 0;
    const pointerDirection =
      activeTooltip.pointerDirection || DEFAULT_POINTER_DIRECTION;
    const pointerDistance =
      (activeTooltip.pointerDistance || 0) + BASE_POINTER_DISTANCE;

    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;

    const targetBox = this.getBoundingBox(
      document.body.querySelector(activeTooltip.selectors)
    );

    const pointerHorizontalOffset =
      CORNER_OFFSET +
      pointerPosition * (tooltipWidth - 2 * (CORNER_OFFSET + POINTER_SIZE));
    const pointerVerticalOffset =
      CORNER_OFFSET +
      pointerPosition * (tooltipHeight - 2 * (CORNER_OFFSET + POINTER_SIZE));

    if (
      [
        TooltipPointerDirectionEnum.up,
        TooltipPointerDirectionEnum.down,
      ].includes(pointerDirection)
    ) {
      if (TooltipPointerDirectionEnum.up === pointerDirection) {
        tooltipStyle.top = `${targetBox.bottom + pointerDistance}px`;
        pointerStyle.top = `${-2 * POINTER_SIZE}px`;
      } else {
        tooltipStyle.top = `${
          targetBox.top - tooltipHeight - pointerDistance
        }px`;
        pointerStyle.top = `${tooltipHeight}px`;
      }
      tooltipStyle.left = `${
        targetBox.midHorizontal - pointerHorizontalOffset - CORNER_OFFSET
      }px`;
      pointerStyle.left = `${pointerHorizontalOffset}px`;
    } else {
      if (TooltipPointerDirectionEnum.left === pointerDirection) {
        tooltipStyle.left = `${targetBox.right + pointerDistance}px`;
        pointerStyle.left = `${-2 * POINTER_SIZE}px`;
      } else {
        tooltipStyle.left = `${
          targetBox.left - tooltipWidth - pointerDistance
        }px`;
        pointerStyle.left = `${tooltipWidth}px`;
      }
      tooltipStyle.top = `${
        targetBox.midVertical - pointerVerticalOffset - CORNER_OFFSET
      }px`;
      pointerStyle.top = `${pointerVerticalOffset}px`;
    }

    if (
      typeof activeTooltip.scrollIntoView === 'boolean'
        ? activeTooltip.scrollIntoView
        : true
    ) {
      tooltipEl.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }

    tooltipEl.focus();
  }

  getBoundingBox(
    element
  ): ClientRect & { midHorizontal: number; midVertical: number } {
    const window = this._windowRefService.nativeWindow;
    const box = element.getBoundingClientRect();

    const left = box.left + window.pageXOffset;
    const right = box.right + window.pageXOffset;
    const top = box.top + window.pageYOffset;
    const bottom = box.bottom + window.pageYOffset;
    const width = box.right - box.left;
    const height = box.bottom - box.top;

    return {
      left,
      right,
      top,
      bottom,
      width,
      height,
      midHorizontal: left + width / 2,
      midVertical: top + height / 2,
    };
  }

  loadNextTooltip() {
    this.activeTooltipIndex++;
    if (this.isOver()) {
      this.dismiss();
    } else {
      this._trackingService.trackEvent(this.trackingObject, this.nextAction, {
        step: this.activeTooltipIndex,
      });
      if (this.isLastTooltip()) {
        // viewed all tooltips, hence completed guided tour
        this._trackingService.trackEvent(
          this.trackingObject,
          this.completedAction
        );
      }
      this.loadActiveTooltip();
    }
  }

  loadPreviousTooltip() {
    this.activeTooltipIndex = Math.max(0, this.activeTooltipIndex - 1);
    this.loadActiveTooltip();
  }

  onOverlayClicked(event: MouseEvent) {
    if (this.isDismissible && event.target === this.overlayRef.nativeElement) {
      this.dismiss();
    }
  }

  dismiss() {
    this._tooltipSequenceService.end();
    this.isActive = false;
    this._trackingService.trackEvent(
      this.trackingObject,
      this.isOver() ? this.doneAction : this.dismissAction,
      {
        step: this.activeTooltipIndex,
      }
    );
  }

  @HostListener('window:keydown', ['$event'])
  keyEventDown(event: KeyboardEvent) {
    if (this.isOver()) {
      return;
    }
    if (event.key === 'Tab') {
      this.isTabPressed = true;
      event.preventDefault();
      if (event.shiftKey) {
        this.loadPreviousTooltip();
      } else {
        this.loadNextTooltip();
      }
    } else if (event.key === 'Escape') {
      if (this.isDismissible) {
        this.dismiss();
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent) {
    if (this.tooltips && this.tooltips[this.activeTooltipIndex]) {
      this.loadActiveTooltip();
    }
  }

  ngOnDestroy() {
    this.tooltipServiceSubscription.unsubscribe();
  }
}
